import { IBaseStore } from '../types'
import { Module, VuexModule, Mutation, Action } from 'vuex-module-decorators'
import {
  IUserRolePermission,
  EUserPermissions,
  IUserChainRole,
  IUserCustomPermission
} from '@zunta/configurations'
import Api from '@/api'

export interface IPermissionsState {
  permissions: IUserRolePermission[];
  roles: IUserChainRole[];
  userCustomPermissions: IUserCustomPermission[];
}

export interface IPermissionsStore extends IBaseStore {
  state: IPermissionsState
}

@Module({
  name: 'permissions',
  namespaced: true
})
export class PermissionsModule extends VuexModule {
  permissions: IUserRolePermission[] = []
  roles: IUserChainRole[] = [];
  userCustomPermissions: IUserCustomPermission[] = [];

  @Mutation
  UPDATE_ROLES_LIST (roles: IUserChainRole[]) {
    this.roles = roles
  }

  @Mutation
  DELETE_ROLE (roleId: number) {
    this.roles = this.roles.filter((role: IUserChainRole) => role.id !== roleId)
  }

  @Mutation
  UPDATE_ROLE (input: { roleId: number, name: string }) {
    for (let i = 0; i < this.roles.length; i++) {
      if (this.roles[i].id === input.roleId) {
        this.roles[i].name = input.name
        break
      }
    }
  }

  @Mutation
  ADD_ROLE (role: IUserChainRole) {
    this.roles.push(role)
  }

  @Mutation
  UPDATE_USER_CUSTOM_PERMISSIONS_LIST (permissions: IUserCustomPermission[]) {
    this.userCustomPermissions = permissions
  }

  @Mutation
  UPDATE_PERMISSIONS_LIST (permissions: IUserRolePermission[]) {
    this.permissions = permissions
  }

  @Mutation
  ADD_PERMISSION (permission: IUserRolePermission) {
    this.permissions.push(permission)
  }

  @Mutation
  DELETE_PERMISSION (input: { roleId: number | string, permission: EUserPermissions }) {
    this.permissions = this.permissions.filter((permission: IUserRolePermission) => {
      return !(permission.role.toString() === input.roleId.toString() &&
        permission.permission === input.permission)
    })
  }

  @Action
  async getPermissions (chainId?: number) {
    const permissions = await Api.permissions.getPermissions({ chainId })
    this.UPDATE_PERMISSIONS_LIST(permissions)
  }

  @Action
  async addPermission (input: { roleId: number | string, permission: EUserPermissions }) {
    const permission = await Api.permissions.addRolePermission(input.roleId, input.permission)
    this.ADD_PERMISSION(permission)
  }

  @Action
  async deletePermission (input: { roleId: number | string, permission: EUserPermissions }) {
    const response = await Api.permissions.deleteRolePermission(input.roleId, input.permission)
    if (response === 'OK') {
      this.DELETE_PERMISSION(input)
    }
  }

  @Action
  async getUserRoles (chainId?: number) {
    const roles = await Api.permissions.getUserRoles({ chainId, isZunta: false })
    this.UPDATE_ROLES_LIST(roles)
  }

  @Action
  async getAdminRoles () {
    const roles = await Api.permissions.getUserRoles({ isZunta: true })
    this.UPDATE_ROLES_LIST(roles)
  }

  @Action
  async createUserRole (name: string) {
    await Api.permissions.createRole({ name })
    // this.ADD_ROLE(role)
  }

  @Action
  async deleteUserRole (roleId: number) {
    const response = await Api.permissions.deleteUserRole(roleId)
    if (response === 'OK') {
      this.DELETE_ROLE(roleId)
    }
    return response
  }

  @Action
  async updateUserRole (input: {roleId: number, name: string}) {
    const response = await Api.permissions.updateUserRole(input.roleId, input.name)
    if (response === 'OK') {
      this.UPDATE_ROLE(input)
    }
  }

  @Action
  async getUserCustomPermissions (userId: number | string) {
    const permissions = await Api.permissions.getUserCustomPermissions(userId)
    this.UPDATE_USER_CUSTOM_PERMISSIONS_LIST(permissions)
  }

  @Action
  async resetUserCustomPermission () {
    this.UPDATE_USER_CUSTOM_PERMISSIONS_LIST([])
  }

  @Action
  async checkAbilityToDeleteRole (roleId: number | string) {
    const ability = await Api.permissions.checkAbilityToDeleteRole(roleId)
    return ability
  }
}
